import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mdiCheckDecagram } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Input, Select, Steps } from "antd";
import { observer } from "mobx-react-lite";

import { api } from "../../api";
import { getCountryName } from "../../api/countries";
import AuthCard from "../../components/auth/AuthCard";
import { signupStore } from "../../stores/signupStore";
import { usePageTitle } from "../../utils/usePageTitle";
import { isValidPhone } from "../../utils/validatePhone";

import "./SignupDetailsPage1.css";

import type { Country } from "../../api/countries";

export const SignupDetailsPage1 = observer(function SignupDetailsPage1() {
  const { t, i18n } = useTranslation();
  usePageTitle(t("signup.details.formHeader"));
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [countries, setCountries] = useState<Country[]>([]);
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!signupStore.token) {
      navigate("/signup/0", { replace: true });
      return;
    }
    api.countries
      .getCountries(i18n.language)
      .then(setCountries)
      .catch(() => {});
    api.countries
      .getCurrentCountry(i18n.language)
      .then((current) => {
        if (current.code) setCountry(current.code);
      })
      .catch(() => {});
  }, [i18n.language, navigate]);

  const countryOptions = countries.map((c) => ({
    value: c.code,
    label: getCountryName(c, i18n.language),
  }));

  const isRussia = country === "RU";
  const phoneValid = isValidPhone(phone);

  function handleContinue() {
    setSubmitted(true);
    if (!firstName || !lastName || !country) return;
    if (isRussia && (!phone || !phoneValid)) return;
    signupStore.setDetails(firstName, lastName, country, phone);
    navigate("/signup/details/2");
  }

  return (
    <AuthCard>
      <span className="signup-details__email-chip">
        <Icon color="#4caf50" path={mdiCheckDecagram} size={0.7} />
        {signupStore.email}
      </span>

      <p className="signup-details__verified">
        {t("signup.details.verifiedText")}
      </p>

      <Steps
        current={0}
        className="signup-details__steps"
        items={[
          { title: t("signup.details.stepInfo") },
          { title: t("signup.details.stepPassword") },
        ]}
      />

      <h1 className="signup-details__form-header">
        {t("signup.details.formHeader")}
      </h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
      >
        <Input
          placeholder={t("signup.details.firstName")}
          size="large"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          autoComplete="given-name"
          status={submitted && !firstName ? "error" : undefined}
        />
        {submitted && !firstName && (
          <div className="signup-details__field-error">
            {t("signup.details.required")}
          </div>
        )}

        <Input
          placeholder={t("signup.details.lastName")}
          size="large"
          style={{ marginTop: 12 }}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          autoComplete="family-name"
          status={submitted && !lastName ? "error" : undefined}
        />
        {submitted && !lastName && (
          <div className="signup-details__field-error">
            {t("signup.details.required")}
          </div>
        )}

        <Select
          placeholder={t("signup.details.country")}
          size="large"
          style={{ width: "100%", marginTop: 12 }}
          value={country}
          onChange={setCountry}
          className="select-search"
          classNames={{ input: "select-search__input" }}
          showSearch={{ optionFilterProp: "label" }}
          options={countryOptions}
          status={submitted && !country ? "error" : undefined}
        />
        {submitted && !country && (
          <div className="signup-details__field-error">
            {t("signup.details.required")}
          </div>
        )}

        {isRussia && (
          <>
            <Input
              placeholder={t("signup.details.phone")}
              size="large"
              style={{ marginTop: 12 }}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              status={
                submitted && (!phone || !phoneValid) ? "error" : undefined
              }
            />
            {submitted && !phone && (
              <div className="signup-details__field-error">
                {t("signup.details.required")}
              </div>
            )}
            {submitted && phone && !phoneValid && (
              <div className="signup-details__field-error">
                {t("signup.details.phoneInvalid")}
              </div>
            )}
          </>
        )}

        <Button
          type="primary"
          size="large"
          block
          style={{ marginTop: 24 }}
          onClick={handleContinue}
        >
          {t("app.continue")}
        </Button>
      </form>
    </AuthCard>
  );
});
